import {until} from 'lit-html/directives/until';
import {classMap} from 'lit-html/directives/class-map';
import {repeat} from 'lit-html/directives/repeat';
import '@modules/external-standards';
import {generalLabels, gradesLabels} from '@modules/i18n-labels';

const cdn = (path) => {
  if (!path) {
    return '';
  }
  return 'https://educate.aofl.com/cdn' + path;
};

// it's possible that not all the fields will be set on the skill,
// this is just to safeguard in case that happens
const getSection = (section, ctx, html) => {
  const sectionTitles = {
    game_description: ctx.__('<tt-1ozGdvWV>', 'Game Description'),
    why_important: ctx.__('<tt-3nzpDL0v>', `Why It's Important`),
  };

  if (ctx.skill[section]) {
    return html`
      <div class="font-weight-bold subtitle mt-4">${until(sectionTitles[section])}</div>
      <p class="mb-4">${until(ctx._r('%r1%', ctx.skill[section]))}</p>
    `;
  }
};

const gradeList = (grades) => {
  const list = [];
  for (let i = 0; i < grades.length; i++) {
    list.push(until(gradesLabels.dynamicGrade(grades[i])));
    if (i < grades.length - 1) {
      list.push(', ');
    }
  }
  return list;
};

const getGradeSection = (ctx, html) => {
  if (ctx.skill.grades?.length) {
    return html`<div class="align-items-center border-bottom py-3">
      <div class="d-inline-block mr-3">${until(gradesLabels.GRADE)}</div>
      <div class="d-inline-block">${gradeList(ctx.skill.grades)}</div>
    </div>`;
  }
};

const getDemoActivitiesSection = (ctx, html) => {
  if (!ctx.isActivityDemoEnabled()) {
    return '';
  }
  if (ctx.activitiesLoading) {
    return html`<div class="demo-activities-section py-3"><loading-ellipses></loading-ellipses></div>`;
  }
  if (!ctx.activityIds?.length) {
    return '';
  }
  const genericThumbnailUrl = ctx.getGenericActivityThumbnailUrl();
  const handleThumbnailError = (e) => {
    const img = e.currentTarget;
    if (!genericThumbnailUrl || img.dataset.fallbackApplied === 'true') {
      return;
    }
    img.dataset.fallbackApplied = 'true';
    img.src = genericThumbnailUrl;
  };
  return html`
    <div class="demo-activities-section border-bottom py-3 mb-3">
      <div class="font-weight-bold subtitle mb-2">${until(ctx.__('<tt-RHZ6n8hZ>', 'Activities'))}</div>
      <p class="demo-activities-subheading mb-3">${until(ctx.__('<tt-BPRayKTY>', 'Click an activity thumbnail to try the activity in a new tab.'))}</p>
      <div class="demo-activities-row">
        ${repeat(ctx.activityIds, (id) => id, (id) => {
          const href = ctx.getActivityDemoUrl(id);
          const thumbnailUrl = ctx.getActivityThumbnailUrl(id);
          const content = thumbnailUrl ?
            html`
              <img class="activity-thumb" src="${thumbnailUrl}" alt="${id}" @error="${handleThumbnailError}" />
              <span class="activity-thumb-id">${id}</span>
            ` :
            html`${id}`;
          const linkClass = thumbnailUrl ?
            'mr-3 mb-1 activity-thumb-link' :
            'd-inline-block px-3 py-1 rounded mr-3 mb-1 text-center activity-id';
          return href ? html`
            <a
              class="${linkClass}"
              href="${href}"
              target="_blank"
              rel="opener"
              aria-label="${until(ctx.i18n._r(ctx.i18n.__('<tt-4fEsf2EN>', 'Try activity %r1%'), id))}"
            >${content}</a>
          ` : html`<span class="${linkClass}">${content}</span>`;
        })}
      </div>
    </div>
  `;
};

// eslint-disable-next-line lit/no-invalid-html
export default (ctx, html) => {
  if (!ctx.skill || Object.keys(ctx.skill).length === 0 || !ctx.skill?.name) {
    return html`<loading-ellipses></loading-ellipses>`;
  }
  return html`
    ${ctx.forModal ? html`
      <div class="border-bottom font-weight-bold pb-4">
        ${ctx.skill.name}.
      </div>
      ` :
      ``}
    ${getGradeSection(ctx, html)}
    <external-standards
      .external-standards="${ctx.skill?.external_standards}"
    ></external-standards>

    ${getDemoActivitiesSection(ctx, html)}

    ${ctx.skill?.video ?
      html`
        <div class="${classMap(ctx.videoWrapper)}">
          <video
            class="d-block w-100 video"
            playsinline=""
            webkit-playsinline
            controlsList="nofullscreen nodownload"
            id="skill-video-${ctx.skill?.id}"
            @click=${ctx.toggleVideo}
            controls
            preload="metadata"
            data-bi-value="${ctx.skill?.id}"
            src="${cdn(ctx.skill.video)}">
          </video>
        <button class="play-btn mx-auto p-0 position-absolute d-inline-block btn"
            @click="${() => ctx.toggleVideo()}">
          <img src="${require('@modules/academy-lesson-details/images/icon-play-btn.webp')}"/>
        </button>
      </div>
      ` : ``
    }
    ${getSection('game_description', ctx, html)}
    ${getSection('why_important', ctx, html)}
    ${ctx.skill.recommended_activities?.length > 0 ? html`
    <button
      class="mb-5 btn btn-primary rounded-lg d-block text-center px-4"
      @click="${ctx.viewRecommendedActivities}"
      data-bi-value="open_offline_material"
    >
      <span class="ai ai-back-arrow text-white flip-y d-inline-block font-size-lg font-weight-bold align-middle"></span>
      <div class="d-inline font-weight-semibold">${until(ctx.__('<tt-Xj0mt0Bu>', 'Making Data Actionable with Recommended Activities'))}</div>
    </button>
    ` : ''}
    ${ctx.skill.printable_worksheets?.length > 0 ? html`
      <div class="text-dark font-weight-semibold pb-2 border-dashed-bottom">${until(ctx.__('<tt-/uB466ix>', 'Printable Worksheets'))}</div>
      ${repeat(ctx.skill.printable_worksheets, (worksheet) => worksheet, (worksheet) => html`
        <div class="border-dashed-bottom d-flex flex-direction-row align-items-center py-3 mb-4">
          <div class="flex-1 pdf-icon-wrapper text-center">
            <img src="${require('@assets/images/pdf.webp')}"/>
          </div>
          <div class="flex-grow-1">
            <div class="text-dark">
              <span class="font-weight-semibold">${worksheet.title}</span> - ${worksheet.subtitle}
            </div>
            <button class="d-block pl-0 py-0 line-height-1 btn btn-link font-size-xs" @click="${() => ctx.viewWorksheet(worksheet)}">${until(generalLabels.download)}</button>
          </div>
        </div>
      `)}
      </div>` : ''}
  `;
};
